import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { api } from '@/api/client'
import { Label } from '@/components/ui/label'
import { reportError } from '@/domains/observability'
import { PasswordInput } from '@/domains/signup'

import { authClient } from './auth-client'
import { ChangePasswordForm } from './change-password-form'
import { GoogleIcon } from './google-icon'
import { useUser } from './use-user'

/** better auth bounces a failed google link back to errorCallbackURL with
 *  ?error=<code>. these are the link-flow codes worth a tailored message (the
 *  common one is a google account whose email differs from the account email,
 *  which we block by keeping accountLinking.allowDifferentEmails at its default).
 *  anything not listed falls back to a generic toast.
 */
const LINK_ERROR_MESSAGES: Record<string, string> = {
  "email_doesn't_match":
    'That Google account uses a different email. Sign in to Google with your account email to connect it.',
  account_already_linked_to_different_user:
    'That Google account is already connected to another account.',
  unable_to_link_account:
    'Could not connect your Google account. Please try again.'
}

/** lets a user run both google and email/password against one account and switch
 *  freely. better auth auto-links google to an existing verified email user on sign-in,
 *  so the work here is the other direction: a google-only user has no password,
 *  and neither google nor password is discoverable to add or remove from
 *  settings. providers comes from /v1/me (the account table). a credential row
 *  means a password exists, a google row means google is linked. both methods
 *  are rows in one card, the password editor expands inline under its row.
 */
export function SignInMethods() {
  const { user, refetch } = useUser()
  const [busy, setBusy] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const linkErrorShown = useRef(false)

  const providers = user?.providers ?? []
  const hasGoogle = providers.includes('google')
  const hasPassword = providers.includes('credential')
  const methodCount = providers.length

  /** surface any ?error that lands here (a rejected google link, or a failed
   *  email-change confirmation that bounced back) as a toast instead of leaving
   *  the user on better auth's bare error page or a stale param, then strip it
   *  so a refresh or back nav doesn't re-fire it. known link codes get a
   *  tailored message, anything else a generic one. */
  useEffect(() => {
    const errorCode = searchParams.get('error')
    if (!errorCode || linkErrorShown.current) return
    linkErrorShown.current = true
    toast.error(
      LINK_ERROR_MESSAGES[errorCode] ??
        'Something went wrong. Please try again.'
    )
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('error')
    nextParams.delete('error_description')
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  function connectGoogle() {
    /** google round-trips through the api host, so the urls must be absolute.
     *  land back on settings (not /signup/finish, which is for fresh signups),
     *  errorCallbackURL routes a rejected link here too so the effect above can
     *  show why instead of dumping the user on the api error page. */
    void authClient.linkSocial({
      provider: 'google',
      callbackURL: `${window.location.origin}/settings`,
      errorCallbackURL: `${window.location.origin}/settings`
    })
  }

  async function disconnect(providerId: 'google' | 'credential') {
    setBusy(true)
    const { error } = await authClient.unlinkAccount({ providerId })
    setBusy(false)

    if (error) {
      /** better auth blocks unlinking the last account so a user can't lock
       *  themselves out. we also hide the control in that case, this catches the
       *  race where the other method was removed on another tab. */
      if (error.code === 'FAILED_TO_UNLINK_LAST_ACCOUNT') {
        toast.error('Keep at least one sign-in method.')
      } else {
        reportError(error)
        toast.error('Could not disconnect. Please try again.')
      }
      return
    }

    await refetch()
    toast.success('Disconnected')
  }

  return (
    <div className='flex w-full flex-col gap-4 rounded-2xl border border-border bg-page p-6'>
      <div>
        <h2 className='text-[16px] font-semibold text-ink'>Sign-in methods</h2>
        <p className='mt-0.5 text-[13px] text-ink-soft'>
          Connect more than one way to sign in and use whichever you like.
        </p>
      </div>

      <div className='flex flex-col gap-3'>
        <MethodRow
          name='Google'
          description={
            hasGoogle ? 'Connected' : 'Use your Google account to sign in.'
          }
          connected={hasGoogle}
          canDisconnect={hasGoogle && methodCount > 1}
          disabled={busy}
          connectIcon={<GoogleIcon />}
          onConnect={connectGoogle}
          onDisconnect={() => disconnect('google')}
        />
        <PasswordRow
          hasPassword={hasPassword}
          canDisconnect={hasPassword && methodCount > 1}
          busy={busy}
          onDisconnect={() => disconnect('credential')}
          onChanged={() => void refetch()}
        />
      </div>
    </div>
  )
}

interface MethodRowProps {
  name: string
  description: string
  connected: boolean
  canDisconnect: boolean
  disabled: boolean
  connectIcon?: React.ReactNode
  onConnect: (() => void) | null
  onDisconnect: () => void
}

function MethodRow({
  name,
  description,
  connected,
  canDisconnect,
  disabled,
  connectIcon,
  onConnect,
  onDisconnect
}: MethodRowProps) {
  return (
    <div className='flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4'>
      <div className='flex flex-col'>
        <span className='text-[14px] font-medium text-ink'>{name}</span>
        <span className='text-[12px] text-ink-soft'>{description}</span>
      </div>
      {connected && canDisconnect && (
        <button
          type='button'
          disabled={disabled}
          onClick={onDisconnect}
          className='w-full shrink-0 cursor-pointer rounded-lg border border-border px-4 py-2 text-[13px] font-medium text-ink transition-colors hover:bg-cream/60 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto'
        >
          Disconnect
        </button>
      )}
      {!connected && onConnect && (
        <button
          type='button'
          disabled={disabled}
          onClick={onConnect}
          className='flex w-full shrink-0 cursor-pointer items-center justify-center gap-2.5 rounded-lg border border-border bg-card px-4 py-2 text-[13px] font-medium text-card-foreground transition-colors hover:bg-cream/60 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto'
        >
          {connectIcon}
          Connect
        </button>
      )}
    </div>
  )
}

interface PasswordRowProps {
  hasPassword: boolean
  canDisconnect: boolean
  busy: boolean
  onDisconnect: () => void
  onChanged: () => void
}

function PasswordRow({
  hasPassword,
  canDisconnect,
  busy,
  onDisconnect,
  onChanged
}: PasswordRowProps) {
  const [editing, setEditing] = useState(false)

  function handleDone() {
    setEditing(false)
    onChanged()
  }

  return (
    <div className='rounded-xl border border-border bg-card'>
      <div className='flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4'>
        <div className='flex flex-col'>
          <span className='text-[14px] font-medium text-ink'>
            Email &amp; password
          </span>
          <span className='text-[12px] text-ink-soft'>
            {hasPassword
              ? 'Enabled'
              : 'Set a password to sign in with your email.'}
          </span>
        </div>
        <div className='flex w-full shrink-0 items-center gap-2 sm:w-auto'>
          {hasPassword && canDisconnect && (
            <button
              type='button'
              disabled={busy}
              onClick={onDisconnect}
              className='flex-1 cursor-pointer rounded-lg border border-border px-4 py-2 text-[13px] font-medium text-ink transition-colors hover:bg-cream/60 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none'
            >
              Disconnect
            </button>
          )}
          {!editing && (
            <button
              type='button'
              onClick={() => setEditing(true)}
              className='flex-1 cursor-pointer rounded-lg border border-border px-4 py-2 text-[13px] font-medium text-ink transition-colors hover:bg-cream/60 sm:flex-none'
            >
              {hasPassword ? 'Change' : 'Set password'}
            </button>
          )}
        </div>
      </div>
      {editing && (
        <div className='border-t border-border px-4 py-4'>
          {hasPassword ? (
            <ChangePasswordForm
              onDone={handleDone}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <SetPasswordForm
              onDone={handleDone}
              onCancel={() => setEditing(false)}
            />
          )}
        </div>
      )}
    </div>
  )
}

function SetPasswordForm({
  onDone,
  onCancel
}: {
  onDone: () => void
  onCancel: () => void
}) {
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(event: React.SyntheticEvent) {
    event.preventDefault()
    setError('')

    if (next.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (next !== confirm) {
      setError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    try {
      await api.post('/v1/me/password', { newPassword: next })
      setNext('')
      setConfirm('')
      toast.success('Password set')
      onDone()
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined
      const serverMessage = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined
      setError(
        serverMessage ?? 'Could not set your password. Please try again.'
      )
      // 4xx are expected here (already set, validation), only escalate the rest
      if (status === undefined || status >= 500) reportError(err)
      // already set means another tab beat us, resync so the ui catches up
      if (status === 409) onDone()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='new-password'>New password</Label>
        <PasswordInput
          id='new-password'
          placeholder='At least 8 characters'
          value={next}
          onChange={setNext}
        />
      </div>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='confirm-password'>Confirm password</Label>
        <PasswordInput
          id='confirm-password'
          value={confirm}
          onChange={setConfirm}
        />
      </div>

      {error && <p className='text-sm text-red-600'>{error}</p>}

      <div className='flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
        <button
          type='button'
          onClick={onCancel}
          className='w-full cursor-pointer rounded-xl px-4 py-2.5 text-[14px] font-medium text-ink-soft transition-colors hover:text-ink sm:w-auto'
        >
          Cancel
        </button>
        <button
          type='submit'
          disabled={next.length === 0 || confirm.length === 0 || submitting}
          className='w-full cursor-pointer rounded-xl bg-accent px-6 py-2.5 text-[14px] font-medium text-white transition-all hover:-translate-y-px hover:bg-[#15296b] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-accent sm:w-auto'
        >
          {submitting ? 'Saving…' : 'Set password'}
        </button>
      </div>
    </form>
  )
}
